import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./ProductAnalysis.module.scss";
import {
  Card,
  Table,
  Select,
  Input,
  Typography,
  Tag,
  Spin,
  Empty,
} from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  getProductAnalysisLanding,
  ProductAnalysisLandingItem,
  getAllCategories,
  getSeasons,
  getAllBrands,
  getAllBranchOffices,
} from "../../../api/Services";
import { useDebouncedValue } from "../../../helpers/FilterSearchHelper";
import {
  isVariationAvailable,
  variationNotAvailableLabel,
} from "../../../helpers/VariationHelper";

const { Title, Text } = Typography;

const fmt = (v: number) =>
  `$${(v ?? 0).toLocaleString("es-AR", { maximumFractionDigits: 0 })}`;
const fmtN = (v: number) => (v ?? 0).toLocaleString("es-AR");

const categoryTree = (categoryName: string | null, parents?: string[]) => {
  const parentsString = (parents || []).filter(Boolean).join("/");
  if (!categoryName) return "—";
  return parentsString ? `${parentsString}/${categoryName}` : categoryName;
};

const Var: React.FC<{ value?: number | null; suffix?: string }> = ({
  value,
  suffix = "%",
}) => {
  if (!isVariationAvailable(value)) return <span>{variationNotAvailableLabel()}</span>;
  const pos = value >= 0;
  return (
    <span
      className={`${styles.variation} ${pos ? styles.positive : styles.negative}`}
    >
      {pos ? <ArrowUpOutlined /> : <ArrowDownOutlined />}{" "}
      {pos ? "+" : ""}
      {value.toFixed(1)}
      {suffix}
    </span>
  );
};

const PAGE_SIZE = 20;
const DEBOUNCE_MS = 350;

interface OptionItem {
  value: number;
  label: string;
}

const ProductAnalysis: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [period, setPeriod] = useState<number>(30);
  const [categoryId, setCategoryId] = useState<number | undefined>();
  const [seasonId, setSeasonId] = useState<number | undefined>();
  const [brandId, setBrandId] = useState<number | undefined>();
  const [branchOfficeId, setBranchOfficeId] = useState<number | undefined>();
  const [supplierExternalIdInput, setSupplierExternalIdInput] = useState("");
  const supplierExternalId = useDebouncedValue(supplierExternalIdInput);
  const [stockFilter, setStockFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"grossSales" | "units" | "margin">(
    "grossSales"
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(0);

  const [data, setData] = useState<ProductAnalysisLandingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [categoryOptions, setCategoryOptions] = useState<OptionItem[]>([]);
  const [seasonOptions, setSeasonOptions] = useState<OptionItem[]>([]);
  const [brandOptions, setBrandOptions] = useState<OptionItem[]>([]);
  const [storeOptions, setStoreOptions] = useState<OptionItem[]>([]);

  const debounceRef = useRef<number | null>(null);
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  useEffect(() => {
    setPage(0);
  }, [supplierExternalId]);

  useEffect(() => {
    (async () => {
      try {
        const cats = await getAllCategories();
        setCategoryOptions(
          (cats || [])
            .filter((c: any) => c?.id && c?.name)
            .map((c: any) => {
              const parentsString = (c.parents || [])
                .filter(Boolean)
                .join("/");
              return {
                value: c.id,
                label: parentsString ? `${parentsString}/${c.name}` : c.name,
              };
            })
        );
      } catch {
        setCategoryOptions([]);
      }
    })();
    (async () => {
      try {
        const ss = await getSeasons(0);
        const list = Array.isArray(ss) ? ss : (ss as any)?.seasons ?? [];
        setSeasonOptions(
          list
            .filter((s: any) => s?.id && s?.name)
            .map((s: any) => ({ value: s.id, label: s.name }))
        );
      } catch {
        setSeasonOptions([]);
      }
    })();
    (async () => {
      try {
        const brands = await getAllBrands();
        setBrandOptions(
          (brands || [])
            .filter((b: any) => b?.id && b?.name)
            .map((b: any) => ({ value: b.id, label: b.name }))
        );
      } catch {
        setBrandOptions([]);
      }
    })();
    (async () => {
      try {
        const stores = await getAllBranchOffices();
        setStoreOptions(
          (stores || [])
            .filter((s: any) => s?.id && s?.name)
            .map((s: any) => ({ value: s.id, label: s.name }))
        );
      } catch {
        setStoreOptions([]);
      }
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const result = await getProductAnalysisLanding({
        search: search || undefined,
        categoryId,
        seasonId,
        brandId,
        branchOfficeId,
        supplierExternalId: supplierExternalId.trim() || undefined,
        period,
        stockFilter,
        sortBy,
        sortOrder,
        page,
        size: PAGE_SIZE,
      });
      if (cancelled) return;
      setData(result.content || []);
      setTotal(result.totalElements || 0);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    search,
    categoryId,
    seasonId,
    brandId,
    branchOfficeId,
    supplierExternalId,
    period,
    stockFilter,
    sortBy,
    sortOrder,
    page,
  ]);

  const columns = useMemo(
    () => [
      {
        title: t("ProductAnalysis-Column-Product"),
        key: "product",
        width: 280,
        render: (_: any, r: ProductAnalysisLandingItem) => (
          <div className={styles.productCell}>
            <Text strong className={styles.productName}>
              {r.name}
            </Text>
            <Text type="secondary" className={styles.productMeta}>
              {[r.externalId, r.brandName].filter(Boolean).join(" · ")}
            </Text>
          </div>
        ),
      },
      {
        title: t("ProductAnalysis-Column-Category"),
        key: "category",
        width: 200,
        render: (_: any, r: ProductAnalysisLandingItem) => (
          <div className={styles.productCell}>
            <Text>{categoryTree(r.categoryName, r.parents)}</Text>
            {r.seasons && r.seasons.length > 0 && (
              <Text type="secondary" className={styles.productMeta}>
                {r.seasons.join(", ")}
              </Text>
            )}
          </div>
        ),
      },
      {
        title: t("ProductAnalysis-Column-GrossSales"),
        key: "grossSales",
        sorter: true,
        sortOrder:
          sortBy === "grossSales"
            ? sortOrder === "asc"
              ? ("ascend" as const)
              : ("descend" as const)
            : undefined,
        align: "right" as const,
        render: (_: any, r: ProductAnalysisLandingItem) => (
          <div className={styles.cellStack}>
            <Text strong>{fmt(r.grossSales)}</Text>
            <Var value={r.grossSalesVar} />
          </div>
        ),
      },
      {
        title: t("ProductAnalysis-Column-Units"),
        key: "units",
        sorter: true,
        sortOrder:
          sortBy === "units"
            ? sortOrder === "asc"
              ? ("ascend" as const)
              : ("descend" as const)
            : undefined,
        align: "right" as const,
        render: (_: any, r: ProductAnalysisLandingItem) => (
          <div className={styles.cellStack}>
            <Text strong>{fmtN(r.units)}</Text>
            <Var value={r.unitsVar} />
          </div>
        ),
      },
      {
        title: t("ProductAnalysis-Column-Margin"),
        key: "margin",
        sorter: true,
        sortOrder:
          sortBy === "margin"
            ? sortOrder === "asc"
              ? ("ascend" as const)
              : ("descend" as const)
            : undefined,
        align: "right" as const,
        render: (_: any, r: ProductAnalysisLandingItem) => (
          <div className={styles.cellStack}>
            <Text strong>{(r.marginPct ?? 0).toFixed(1)}%</Text>
            {r.marginPctLY > 0 && <Var value={r.marginVar} suffix=" pp" />}
          </div>
        ),
      },
      {
        title: t("ProductAnalysis-Column-Stock"),
        key: "stock",
        align: "right" as const,
        width: 160,
        render: (_: any, r: ProductAnalysisLandingItem) => (
          <div className={styles.cellStack}>
            <Text strong>{fmtN(r.stock)}</Text>
            {r.hasBreak && (
              <Tag color="red" style={{ marginTop: 2 }}>
                {t("ProductAnalysis-Tag-WithBreak")}
              </Tag>
            )}
            {r.daysOfInventory !== null && r.daysOfInventory !== undefined && (
              <Text type="secondary" className={styles.productMeta}>
                {r.daysOfInventory} {t("ProductAnalysis-Suffix-DaysInventory")}
              </Text>
            )}
          </div>
        ),
      },
    ],
    [sortBy, sortOrder, t]
  );

  const handleTableChange = (pagination: any, _filters: any, sorter: any) => {
    if (sorter && sorter.columnKey) {
      const next = sorter.columnKey as "grossSales" | "units" | "margin";
      const order: "asc" | "desc" =
        sorter.order === "ascend" ? "asc" : "desc";
      if (next !== sortBy || order !== sortOrder) {
        setSortBy(next);
        setSortOrder(order);
        setPage(0);
        return;
      }
    }
    if (pagination?.current && pagination.current - 1 !== page) {
      setPage(pagination.current - 1);
    }
  };

  return (
    <div className={styles.wrapper}>
      <Title level={3} className={styles.pageTitle}>
        {t("ProductAnalysis-Title")}
      </Title>

      <Card className={styles.filtersCard} size="small">
        <div className={styles.filtersRow}>
          <Input
            placeholder={t("ProductAnalysis-SearchPlaceholder")}
            prefix={<SearchOutlined />}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className={styles.searchInput}
            allowClear
          />
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>
              {t("ProductAnalysis-Period")}
            </Text>
            <Select
              value={period}
              onChange={(v) => {
                setPeriod(v);
                setPage(0);
              }}
              className={styles.filterSelect}
              options={[
                { value: 30, label: t("ProductAnalysis-Period-30") },
                { value: 60, label: t("ProductAnalysis-Period-60") },
                { value: 90, label: t("ProductAnalysis-Period-90") },
                { value: 180, label: t("ProductAnalysis-Period-180") },
              ]}
            />
          </div>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>
              {t("ProductAnalysis-Category")}
            </Text>
            <Select
              placeholder={t("ProductAnalysis-AllOptions-F")}
              allowClear
              showSearch
              optionFilterProp="label"
              value={categoryId}
              onChange={(v) => {
                setCategoryId(v);
                setPage(0);
              }}
              className={styles.filterSelect}
              options={categoryOptions}
            />
          </div>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>
              {t("ProductAnalysis-Season")}
            </Text>
            <Select
              placeholder={t("ProductAnalysis-AllOptions-F")}
              allowClear
              showSearch
              optionFilterProp="label"
              value={seasonId}
              onChange={(v) => {
                setSeasonId(v);
                setPage(0);
              }}
              className={styles.filterSelect}
              options={seasonOptions}
            />
          </div>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>
              {t("ProductAnalysis-Brand")}
            </Text>
            <Select
              placeholder={t("ProductAnalysis-AllOptions-F")}
              allowClear
              showSearch
              optionFilterProp="label"
              value={brandId}
              onChange={(v) => {
                setBrandId(v);
                setPage(0);
              }}
              className={styles.filterSelect}
              options={brandOptions}
            />
          </div>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>
              {t("ProductAnalysis-Store")}
            </Text>
            <Select
              placeholder={t("ProductAnalysis-AllOptions-F")}
              allowClear
              showSearch
              optionFilterProp="label"
              value={branchOfficeId}
              onChange={(v) => {
                setBranchOfficeId(v);
                setPage(0);
              }}
              className={styles.filterSelect}
              options={storeOptions}
            />
          </div>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>
              {t("ProductAnalysis-Supplier")}
            </Text>
            <Input
              placeholder={t("ProductAnalysis-SupplierExternalIdPlaceholder")}
              allowClear
              value={supplierExternalIdInput}
              onChange={(e) => setSupplierExternalIdInput(e.target.value)}
              className={styles.filterSelect}
            />
          </div>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>
              {t("ProductAnalysis-Stock")}
            </Text>
            <Select
              value={stockFilter}
              onChange={(v) => {
                setStockFilter(v);
                setPage(0);
              }}
              className={styles.filterSelect}
              options={[
                { value: "all", label: t("ProductAnalysis-AllOptions-M") },
                { value: "low", label: t("ProductAnalysis-Stock-Low") },
                { value: "break", label: t("ProductAnalysis-Stock-Break") },
                { value: "risk", label: t("ProductAnalysis-Stock-Risk") },
              ]}
            />
          </div>
        </div>
      </Card>

      <Card className={styles.tableCard}>
        <Spin spinning={loading}>
          <Table
            columns={columns as any}
            dataSource={data.map((p) => ({ ...p, key: p.id }))}
            pagination={{
              pageSize: PAGE_SIZE,
              current: page + 1,
              total,
              showSizeChanger: false,
            }}
            size="small"
            className={styles.dataTable}
            onChange={handleTableChange}
            onRow={(record) => ({
              onClick: () =>
                navigate(`/commercial-analysis/products/${record.id}`),
              style: { cursor: "pointer" },
            })}
            scroll={{ x: 1100 }}
            locale={{
              emptyText: loading ? (
                <span />
              ) : (
                <Empty description={t("ProductAnalysis-Empty-NoResults")} />
              ),
            }}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default ProductAnalysis;
